import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../config/api';
import logo from '../../assets/logo.png';
import { recordSwipe } from '../../utils/streaks';
import './SessionScreen.css';

export default function SessionScreen() {
  const navigate = useNavigate();
  const { code } = useParams();
  const [, setSession] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(false);
  const [round, setRound] = useState(1);
  const [waitingForOthers, setWaitingForOthers] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [priceFilter, setPriceFilter] = useState(null);
  const [chainFilter, setChainFilter] = useState(null);
  const [userFavorites, setUserFavorites] = useState([]);
  const [swipeStats, setSwipeStats] = useState({ loved: [], liked: [], disliked: [] });
  const [showFatigueModal, setShowFatigueModal] = useState(false);
  const [quickPickLoading, setQuickPickLoading] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const cardRef = useRef(null);

  // Decision fatigue detection
  useEffect(() => {
    const sessionCount = parseInt(sessionStorage.getItem('sessionCount') || '0');
    const lastSessionTime = parseInt(sessionStorage.getItem('lastSessionTime') || '0');
    const now = Date.now();
    
    // If 3+ sessions in last hour, show fatigue modal
    if (sessionCount >= 3 && (now - lastSessionTime) < 3600000) {
      setShowFatigueModal(true);
    }
    
    sessionStorage.setItem('sessionCount', String(sessionCount + 1));
    sessionStorage.setItem('lastSessionTime', String(now));
  }, []);
  
  const SWIPE_X_THRESHOLD = 100;
  const SWIPE_Y_THRESHOLD = 80;

  const loadSession = useCallback(async () => {
    try {
      const { data } = await api.get(`/sessions/${code}`);
      setSession(data.session);
      setRestaurants(data.session.currentRestaurants || []);
      setRound(data.session.currentRound || 1);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load session:', error);
      alert('Session not found');
      navigate('/');
    }
  }, [code, navigate]);

  useEffect(() => {
    loadSession();
    loadUserFavorites();
  }, [loadSession]);

  async function loadUserFavorites() {
    try {
      const { data } = await api.get('/favorites');
      setUserFavorites(data.favorites || []);
    } catch (err) {
      // Guest users won't have favorites - that's fine
      setUserFavorites([]);
    }
  }

  // Reset image states when card changes
  useEffect(() => {
    setImageLoading(true);
    setImageError(false);
  }, [currentIndex]);

  const handlePointerDown = (e) => {
    if (swiping) return;
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX || e.touches?.[0]?.clientX || 0,
      y: e.clientY || e.touches?.[0]?.clientY || 0
    };
  };

  const handlePointerMove = (e) => {
    if (!isDragging || swiping) return;
    const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
    const currentY = e.clientY || e.touches?.[0]?.clientY || 0;
    const deltaX = currentX - dragStart.current.x;
    const deltaY = currentY - dragStart.current.y;
    setSwipeOffset({ x: deltaX, y: deltaY });
  };

  const handlePointerUp = () => {
    if (!isDragging || swiping) return;
    setIsDragging(false);
    
    const { x, y } = swipeOffset;
    
    // Swipe UP = LOVE
    if (y < -SWIPE_Y_THRESHOLD && Math.abs(x) < SWIPE_X_THRESHOLD) {
      animateSwipe('love');
    }
    // Swipe RIGHT = LIKE
    else if (x > SWIPE_X_THRESHOLD) {
      animateSwipe('like');
    }
    // Swipe LEFT = DISLIKE
    else if (x < -SWIPE_X_THRESHOLD) {
      animateSwipe('dislike');
    }
    // Not enough - snap back
    else {
      setSwipeOffset({ x: 0, y: 0 });
    }
  };

  const animateSwipe = (direction) => {
    const finalX = direction === 'like' ? 500 : direction === 'dislike' ? -500 : 0;
    const finalY = direction === 'love' ? -500 : 0;
    
    setIsAnimatingOut(true);
    setSwipeOffset({ x: finalX, y: finalY });
    
    setTimeout(() => {
      handleSwipe(direction);
    }, 300);
  };

  async function handleSwipe(direction) {
    if (swiping || currentIndex >= restaurants.length) return;

    setSwiping(true);
    const restaurant = restaurants[currentIndex];

    // Track swipe stats for insights
    setSwipeStats(prev => {
      const newStats = { ...prev };
      if (direction === 'love') newStats.loved = [...prev.loved, restaurant];
      else if (direction === 'like') newStats.liked = [...prev.liked, restaurant];
      else if (direction === 'dislike') newStats.disliked = [...prev.disliked, restaurant];
      return newStats;
    });

    try {
      const { data } = await api.post(`/sessions/${code}/swipe`, {
        restaurantId: restaurant.id,
        direction,
        round
      });

      recordSwipe();

      if (data.userDone && !data.allDone) {
        setWaitingForOthers(true);
        setCanUndo(false);
      } else {
        setCanUndo(true);
      }

      if (data.allDone && data.finalPicks) {
        // Store swipe stats in sessionStorage for insights display
        sessionStorage.setItem('lastSwipeStats', JSON.stringify(swipeStats));
        navigate(`/results/${code}`);
      } else if (data.allDone && data.nextRound) {
        // Next round - use the advancing restaurants from the response
        setRound(data.nextRound);
        setRestaurants(data.roundResult?.advancing || []);
        setCurrentIndex(0);
        setCanUndo(false);
        setWaitingForOthers(false);
      } else {
        setCurrentIndex(prev => prev + 1);
      }
    } catch (error) {
      console.error('Swipe error:', error);
      alert('Failed to record swipe. Please try again.');
    } finally {
      setSwiping(false);
      setIsAnimatingOut(false);
      setSwipeOffset({ x: 0, y: 0 });
    }
  }

  async function handleUndo() {
    if (!canUndo || currentIndex <= 0 || swiping) return;
    
    const prevIndex = currentIndex - 1;
    const prevRestaurant = restaurants[prevIndex];
    if (!prevRestaurant) return;

    try {
      await api.post(`/sessions/${code}/undo-swipe`, {
        restaurantId: prevRestaurant.id,
        round
      });
      setCurrentIndex(prevIndex);
      setCanUndo(false);
      alert('Swipe undone!');
    } catch {
      alert('Can\'t undo that swipe');
    }
  }

  async function handleQuickPick() {
    if (quickPickLoading || !currentRestaurant) return;

    const confirmed = window.confirm(`Skip swiping and order from ${currentRestaurant.name}?`);
    if (!confirmed) return;

    setQuickPickLoading(true);
    try {
      await api.post(`/sessions/${code}/quick-pick`, {
        restaurantId: currentRestaurant.id,
      });

      // Navigate to results with this single pick
      navigate(`/results/${code}`, {
        state: {
          finalPicks: [{ restaurant: currentRestaurant, score: 999, rank: 1 }],
          quickPick: true,
        },
      });
    } catch (error) {
      alert('Failed to process quick pick');
      setQuickPickLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="session-screen">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading session...</p>
        </div>
      </div>
    );
  }

  if (waitingForOthers) {
    return (
      <div className="session-screen">
        <header className="session-header">
          <img src={logo} alt="Hangrily" className="session-logo-btn" onClick={() => navigate('/')} style={{ height: '26px', cursor: 'pointer' }} />
          <h1 className="session-code">Round {round}</h1>
        </header>
        <div className="waiting-container">
          <div className="waiting-animation">⏳</div>
          <h2>Waiting for others...</h2>
          <p>Your partner is still swiping</p>
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  const currentRestaurant = restaurants[currentIndex];
  const progress = ((currentIndex / restaurants.length) * 100).toFixed(0);

  if (!currentRestaurant) {
    return (
      <div className="session-screen">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Processing results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="session-screen">
      <header className="session-header">
        <img src={logo} alt="Hangrily" className="session-logo-btn" onClick={() => navigate('/')} style={{ height: '26px', cursor: 'pointer' }} />
        <div className="session-info">
          <span className="session-code">{code}</span>
          <span className="round-indicator">Round {round}</span>
        </div>
        <div className="progress-indicator">{currentIndex + 1}/{restaurants.length}</div>
      </header>

      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
      </div>

      {/* Quick Filters Bar */}
      <div className="quick-filters-bar">
        <button 
          className="filter-toggle-btn"
          onClick={() => setShowFilters(!showFilters)}
        >
          🎛️ Filters {showFilters ? '▼' : '▶'}
        </button>
        {showFilters && (
          <div className="filters-panel">
            <button 
              className={`filter-chip ${priceFilter === 1 ? 'active' : ''}`}
              onClick={() => setPriceFilter(priceFilter === 1 ? null : 1)}
            >
              $ Only
            </button>
            <button 
              className={`filter-chip ${priceFilter === 2 ? 'active' : ''}`}
              onClick={() => setPriceFilter(priceFilter === 2 ? null : 2)}
            >
              $$ Only
            </button>
            <button 
              className={`filter-chip ${chainFilter === 'local' ? 'active' : ''}`}
              onClick={() => setChainFilter(chainFilter === 'local' ? null : 'local')}
            >
              📍 Local Only
            </button>
            <button 
              className={`filter-chip ${chainFilter === 'chains' ? 'active' : ''}`}
              onClick={() => setChainFilter(chainFilter === 'chains' ? null : 'chains')}
            >
              🏢 Chains Only
            </button>
          </div>
        )}
      </div>

      <main className="session-main">
        <div 
          ref={cardRef}
          className={`restaurant-card ${isDragging ? 'dragging' : ''}`}
          style={{
            transform: `translate(${swipeOffset.x}px, ${swipeOffset.y}px) rotate(${swipeOffset.x * 0.1}deg)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease-out',
            cursor: isDragging ? 'grabbing' : 'grab',
            opacity: isAnimatingOut ? 1 : 1,
            visibility: isAnimatingOut ? 'visible' : 'visible'
          }}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={() => isDragging && handlePointerUp()}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
        >
          {/* Swipe indicators */}
          <div className="swipe-indicator like-indicator" style={{ opacity: Math.max(0, Math.min(1, swipeOffset.x / 100)) }}>
            👍 LIKE
          </div>
          <div className="swipe-indicator dislike-indicator" style={{ opacity: Math.max(0, Math.min(1, -swipeOffset.x / 100)) }}>
            👎 NOPE
          </div>
          <div className="swipe-indicator love-indicator" style={{ opacity: Math.max(0, Math.min(1, -swipeOffset.y / 80)) }}>
            ❤️ LOVE
          </div>

          {/* Quick Pick Button */}
          <button
            className="quick-pick-btn"
            onClick={handleQuickPick}
            disabled={quickPickLoading}
          >
            🚀 Order Now
          </button>

          {currentRestaurant.image && !imageError ? (
            <>
              <div className="card-image" style={{ backgroundImage: `url(${currentRestaurant.image})` }}>
                <div className="card-overlay"></div>
                {currentRestaurant.isOpen === false && (
                  <div className="closed-overlay">
                    <span className="closed-text">Currently Closed</span>
                  </div>
                )}
                {/* Favorites Badge */}
                {userFavorites.some(fav => fav.restaurantId === currentRestaurant.id) && (
                  <div className="favorite-badge">
                    ⭐ In Your Favorites
                  </div>
                )}
                {/* Social Proof Badge */}
                {currentRestaurant.reviewCount > 100 && (
                  <div className="social-proof-badge">
                    🔥 {currentRestaurant.reviewCount}+ reviews
                  </div>
                )}
              </div>
              <img 
                src={currentRestaurant.image} 
                alt="" 
                style={{ display: 'none' }}
                onLoad={() => setImageLoading(false)}
                onError={() => { setImageError(true); setImageLoading(false); }}
              />
              {imageLoading && (
                <div className="image-loading-overlay">
                  <div className="spinner"></div>
                </div>
              )}
            </>
          ) : (
            <div className="card-image-placeholder">
              <span className="placeholder-emoji">🍽️</span>
            </div>
          )}
          
          <div className="card-content">
            <h2 className="restaurant-name">{currentRestaurant.name}</h2>
            
            <div className="restaurant-meta">
              <span className="cuisine-badge">{currentRestaurant.cuisine}</span>
              <span className="price-badge">{'$'.repeat(currentRestaurant.priceLevel || 2)}</span>
              {currentRestaurant.rating > 0 && (
                <span className="rating-badge">⭐ {currentRestaurant.rating.toFixed(1)}</span>
              )}
              {currentRestaurant.source !== 'mock' && (
                <span className={`chain-badge ${currentRestaurant.isChain ? 'chain-badge--chain' : 'chain-badge--local'}`}>
                  {currentRestaurant.isChain ? '🏢 Chain' : '📍 Local'}
                </span>
              )}
            </div>

            {currentRestaurant.address && (
              <p className="restaurant-address">📍 {currentRestaurant.address}</p>
            )}

            {currentRestaurant.distance && (
              <p className="restaurant-distance">🚗 {currentRestaurant.distance}</p>
            )}
          </div>
        </div>

        <div className="swipe-buttons">
          <button 
            className="swipe-btn dislike-btn"
            onClick={() => handleSwipe('dislike')}
            disabled={swiping}
          >
            <span className="btn-icon">👎</span>
            <span className="btn-label">Dislike</span>
          </button>

          <button 
            className="swipe-btn like-btn"
            onClick={() => handleSwipe('like')}
            disabled={swiping}
          >
            <span className="btn-icon">👍</span>
            <span className="btn-label">Like</span>
          </button>

          <button 
            className="swipe-btn love-btn"
            onClick={() => handleSwipe('love')}
            disabled={swiping}
          >
            <span className="btn-icon">❤️</span>
            <span className="btn-label">Love</span>
          </button>
        </div>

        {canUndo && currentIndex > 0 && (
          <button className="undo-btn" onClick={handleUndo}>
            ↩ Undo Last Swipe
          </button>
        )}
      </main>

      {/* Decision Fatigue Modal */}
      {showFatigueModal && (
        <>
          <div className="modal-overlay" onClick={() => setShowFatigueModal(false)} />
          <div className="modal-content fatigue-modal">
            <div style={{ fontSize: '48px', textAlign: 'center', marginBottom: '16px' }}>😵</div>
            <h2 style={{ marginBottom: '12px', fontSize: '22px', fontWeight: '700' }}>
              Taking a lot of decisions today?
            </h2>
            <p style={{ marginBottom: '20px', color: '#888', fontSize: '14px', lineHeight: '1.6' }}>
              We noticed you've started multiple sessions recently. Decision fatigue is real! 
              Here are some tips:
            </p>
            <ul style={{ marginBottom: '20px', color: '#aaa', fontSize: '13px', lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>Trust your gut - your first instinct is often right</li>
              <li>Narrow down your preferences before swiping</li>
              <li>Take a break and come back with fresh eyes</li>
              <li>Try "Surprise Me" mode to let us decide</li>
            </ul>
            <button 
              className="btn btn-primary"
              style={{ width: '100%' }}
              onClick={() => setShowFatigueModal(false)}
            >
              Got it, let's continue
            </button>
          </div>
        </>
      )}
    </div>
  );
}
